import { v4 as uuidv4 } from 'uuid';

import { License, ResolverTag } from '@ps/web-core-enums';
import { Translation } from '@ps/web-shared-components';
import { StarsInfo } from '@ps/web-shared-ui';
import SumsubWebSdk from '@sumsub/websdk-react';
import type { EventPayload } from '@sumsub/websdk/types/types';
import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import LoadingIndicator from '@swf/components/LoadingIndicator/LoadingIndicator';
import ResolverLink from '@swf/components/ResolverLink/ResolverLink';
import StarsButton from '@swf/components/StarsButton/StarsButton';
import useRequest from '@swf/core/hooks/useRequest';
import { select } from '@swf/core/store';
import type { Dispatch } from '@swf/core/store';

import {
  TXTCLI_OK,
  TXTCLI_Please_try_again_laterX,
  TXTCLI_Verification_Failed,
  TXTCLI_We_apologize_for_any_inconveni_ELL_1,
  TXTMOB_Bank_Statement,
  TXTWCL_Machines_make_mistakes_tooX_If_ELL,
  TXTWCL_SorryX_we_canXt_verify_your_do_ELL,
  TXTWCL_UnfortunatelyX_we_couldnXt_ver_ELL
} from './SumSubProvider.messages.json';
import { useRequestToken } from './useRequestToken';
import { useToken } from './useToken';
import { sendStartVerificationRequest } from './utils';

enum VerifyStep {
  Initial,
  ApplicantSubmitted
}

enum EventName {
  ApplicantSubmitted = 'idCheck.onApplicantSubmitted',
  ApplicantReviewComplete = 'idCheck.applicantReviewComplete',
  ApplicantReviewStatus = 'idCheck.applicantStatus',
  ReadyToLoad = 'idCheck.onReady',
  onStepInitiated = 'idCheck.onStepInitiated'
}

enum ErrorCode {
  SumSubChangeLevelName = 'sumsubChangeLevelName'
}

type Message = EventPayload<EventName.ApplicantReviewStatus> & {
  reviewResult: {
    reviewAnswer: string | Record<string, unknown>;
  };
  attemptCnt: number;
};

interface StepInitiatedMessage {
  idDocSetType: string;
}

interface Props {
  showButton?: boolean;
  onClose?(): void;
  onComplete?(): void;
  onSuccess?(): void;
  onFailed?(): void;
  showCompleteButton?: boolean;
  onJourneyComplete?(): void;
  onJourneyInitialized?(): void;
  onJourneyRefId?(jid: string | undefined): void;
  onSdkInit?(): void;
}

const SumSubProvider: React.FC<Props> = ({
  onComplete,
  showButton,
  onSuccess,
  onFailed,
  onJourneyComplete,
  onJourneyInitialized,
  onJourneyRefId,
  onSdkInit
}) => {
  const [verifyStep, setVerifyStep] = useState(VerifyStep.Initial);
  const [reviewResult, setReviewResult] = useState<Message | null>(null);
  const { token, hasError, errorCode } = useToken();
  const request = useRequest();
  const requestToken = useRequestToken();
  const locale = useSelector(select.locale.get);
  const email = useSelector(select.account.getEmail);
  const lincense = useSelector(select.account.getLicense);
  const getNewToken = useCallback(() => requestToken(), [requestToken]);
  const { formatMessage } = useIntl();

  const onCompleteHandler = useCallback(() => onComplete?.(), [onComplete]);

  const { localSettings } = useDispatch<Dispatch>();

  const handleAplicantSubmitted = () => {
    setVerifyStep(VerifyStep.ApplicantSubmitted);
    onJourneyComplete?.();
    localSettings.set({ documentsSubmitted: true });
  };

  const handleStepInitiated = (message: StepInitiatedMessage) => {
    message.idDocSetType === 'IDENTITY' && onJourneyInitialized?.();
  };

  const customTranslations =
    License.CZ === lincense
      ? {
          // old sdk
          steps: {
            title: {
              QUESTIONNAIRE: formatMessage(TXTMOB_Bank_Statement)
            }
          },
          // new sdk
          stepTitles: {
            QUESTIONNAIRE: { short: formatMessage(TXTMOB_Bank_Statement) }
          }
        }
      : undefined;

  useEffect(() => {
    if (
      onSuccess &&
      reviewResult?.reviewResult?.reviewAnswer === 'GREEN' &&
      reviewResult?.reviewStatus === 'completed'
    ) {
      setTimeout(() => onSuccess(), 4000);
    }
    if (
      onFailed &&
      reviewResult?.reviewResult?.reviewAnswer === 'RED' &&
      reviewResult?.reviewStatus === 'completed'
    ) {
      setTimeout(() => onFailed(), 4000);
    }
  }, [reviewResult, onSuccess, onFailed]);

  useEffect(() => {
    if (onSdkInit && token) {
      onSdkInit();
    }
  }, [token, onSdkInit]);

  return hasError ? (
    errorCode === ErrorCode.SumSubChangeLevelName ? (
      <StarsInfo type="error">
        <Translation tagName="b" {...TXTWCL_UnfortunatelyX_we_couldnXt_ver_ELL} />
        <Translation
          tagName="p"
          {...TXTWCL_Machines_make_mistakes_tooX_If_ELL}
          values={{
            a: (msg: string) => (
              <ResolverLink resolverTag={ResolverTag.HelpCenter}>{msg}</ResolverLink>
            )
          }}
        />
      </StarsInfo>
    ) : (
      <StarsInfo type="error">
        <Translation tagName="b" {...TXTCLI_Verification_Failed} />
        <Translation tagName="p" {...TXTWCL_SorryX_we_canXt_verify_your_do_ELL} />
        <Translation {...TXTCLI_We_apologize_for_any_inconveni_ELL_1} />
        <Translation tagName="p" {...TXTCLI_Please_try_again_laterX} />
      </StarsInfo>
    )
  ) : (
    <LoadingIndicator loading={!token}>
      <SumsubWebSdk
        accessToken={token}
        expirationHandler={getNewToken}
        onMessage={(event, message) => {
          switch (event) {
            case EventName.ReadyToLoad: {
              onJourneyRefId?.(uuidv4());
              sendStartVerificationRequest({ request }, 'Sumsub');
              break;
            }
            case EventName.ApplicantSubmitted:
              handleAplicantSubmitted();
              break;
            case EventName.ApplicantReviewStatus:
              setReviewResult(message as Message);
              break;
            case EventName.onStepInitiated:
              handleStepInitiated(message as StepInitiatedMessage);
              break;
            default:
              break;
          }
        }}
        config={{
          lang: locale,
          email,
          i18n: customTranslations
        }}
        options={{
          addViewportTag: false,
          adaptIframeHeight: true
        }}
      />
      {verifyStep === VerifyStep.ApplicantSubmitted && showButton && (
        <StarsButton block translation={TXTCLI_OK} onClick={onCompleteHandler} />
      )}
    </LoadingIndicator>
  );
};

export default SumSubProvider;
